import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Brain, DollarSign, Users, Target, Info, Sparkles,
  RefreshCw, Download, Database
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import VectorAnalyticsDashboard from './VectorAnalyticsDashboard';

const AIAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const [churnData, setChurnData] = useState(null);
  const [featureData, setFeatureData] = useState(null);
  const [activeTab, setActiveTab] = useState('pricing');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [pricing, churn, features] = await Promise.all([
        axios.get(`${API}/ai-analytics/dynamic-pricing`),
        axios.get(`${API}/ai-analytics/churn-prediction`),
        axios.get(`${API}/ai-analytics/feature-popularity`)
      ]);
      
      setPricingData(pricing.data);
      setChurnData(churn.data);
      setFeatureData(features.data);
    } catch (error) {
      toast.error('Failed to load AI analytics');
    } finally {
      setLoading(false);
    }
  };

  const getInsightIcon = (type) => {
    switch(type) {
      case 'success': return <CheckCircle className="h-4 w-4" />;
      case 'warning': return <AlertTriangle className="h-4 w-4" />;
      case 'info': return <Info className="h-4 w-4" />;
      case 'opportunity': return <Target className="h-4 w-4" />;
      default: return <Sparkles className="h-4 w-4" />;
    }
  };

  const getInsightColor = (type) => {
    switch(type) {
      case 'success': return 'bg-green-100 text-green-800 border-green-200';
      case 'warning': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'opportunity': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading && !pricingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="h-8 w-8 text-teal-600" />
            AI Analytics
          </h1>
          <p className="text-gray-600 mt-1">Data-driven insights powered by statistical AI</p>
        </div>
        <Button onClick={fetchAllData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total MRR</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${pricingData?.summary?.total_monthly_recurring_revenue?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-gray-500 mt-1">Monthly Recurring Revenue</p>
              </div>
              <DollarSign className="h-10 w-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">At-Risk Members</p>
                <p className="text-2xl font-bold text-orange-600">
                  {churnData?.summary?.high_risk_count || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Need immediate attention</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Health Score</p>
                <p className="text-2xl font-bold text-teal-600">
                  {churnData?.summary?.overall_health_score?.toFixed(1) || '0.0'}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Member retention health</p>
              </div>
              <CheckCircle className="h-10 w-10 text-teal-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pricing">
            <DollarSign className="h-4 w-4 mr-2" />
            Dynamic Pricing
          </TabsTrigger>
          <TabsTrigger value="churn">
            <Users className="h-4 w-4 mr-2" />
            Churn Prediction
          </TabsTrigger>
          <TabsTrigger value="features">
            <Target className="h-4 w-4 mr-2" />
            Feature Popularity
          </TabsTrigger>
          <TabsTrigger value="vectors">
            <Database className="h-4 w-4 mr-2" />
            Vector Analytics
          </TabsTrigger>
        </TabsList>

        {/* Vector Analytics Tab */}
        <TabsContent value="vectors">
          <VectorAnalyticsDashboard />
        </TabsContent>

        {/* Dynamic Pricing Tab */}
        <TabsContent value="pricing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Dynamic Pricing Recommendations</CardTitle>
              <CardDescription>
                AI-analyzed pricing strategies based on plan performance and market position
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {pricingData?.recommendations?.map((rec) => (
                  <Card key={rec.plan_id} className="border-2">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{rec.plan_name}</h3>
                          <p className="text-2xl font-bold text-teal-600 mt-1">
                            ${rec.current_price.toFixed(2)}/month
                          </p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800">
                          {rec.metrics.market_position}
                        </Badge>
                      </div>

                      {/* Metrics Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Members</p>
                          <p className="text-lg font-semibold">{rec.metrics.member_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Uptake Rate</p>
                          <p className="text-lg font-semibold">{rec.metrics.uptake_rate}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">MRR</p>
                          <p className="text-lg font-semibold">${rec.metrics.monthly_recurring_revenue.toFixed(0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Success Rate</p>
                          <p className="text-lg font-semibold">{rec.metrics.payment_success_rate.toFixed(0)}%</p>
                        </div>
                      </div>

                      {/* AI Insights */}
                      <div className="space-y-2 mb-4">
                        {rec.ai_insights.map((insight, idx) => (
                          <div 
                            key={idx}
                            className={`flex items-start gap-2 p-3 rounded-lg border ${getInsightColor(insight.type)}`}
                          >
                            {getInsightIcon(insight.type)}
                            <span className="text-sm">{insight.message}</span>
                          </div>
                        ))}
                      </div>

                      {/* Pricing Suggestions */}
                      {rec.pricing_suggestions.length > 0 && (
                        <div className="border-t pt-4">
                          <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-teal-600" />
                            AI Pricing Suggestions
                          </h4>
                          <div className="space-y-2">
                            {rec.pricing_suggestions.map((suggestion, idx) => (
                              <div key={idx} className="bg-teal-50 p-3 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  {suggestion.type === 'increase' ? (
                                    <TrendingUp className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <TrendingDown className="h-4 w-4 text-orange-600" />
                                  )}
                                  <span className="font-semibold text-gray-900">
                                    ${suggestion.suggested_price.toFixed(2)}/month
                                  </span>
                                  <span className="text-xs text-gray-600">
                                    ({suggestion.type === 'increase' ? '+' : ''}{((suggestion.suggested_price - rec.current_price) / rec.current_price * 100).toFixed(1)}%)
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700">{suggestion.reason}</p>
                                <p className="text-xs text-teal-700 mt-1">{suggestion.expected_impact}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Churn Prediction Tab */}
        <TabsContent value="churn" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Member Churn Risk Analysis</CardTitle>
              <CardDescription>
                Identify at-risk members and receive retention recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">{churnData?.summary?.high_risk_count || 0}</p>
                  <p className="text-sm text-gray-600">High Risk</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-3xl font-bold text-orange-600">{churnData?.summary?.medium_risk_count || 0}</p>
                  <p className="text-sm text-gray-600">Medium Risk</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-600">{churnData?.summary?.low_risk_count || 0}</p>
                  <p className="text-sm text-gray-600">Low Risk</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{churnData?.summary?.healthy_count || 0}</p>
                  <p className="text-sm text-gray-600">Healthy</p>
                </div>
              </div>

              {/* At-Risk Members List */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">At-Risk Members</h3>
                {churnData?.at_risk_members?.slice(0, 10).map((member) => (
                  <Card key={member.member_id} className={`border-l-4 ${
                    member.risk_level === 'high' ? 'border-l-red-500' :
                    member.risk_level === 'medium' ? 'border-l-orange-500' :
                    'border-l-yellow-500'
                  }`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{member.member_name}</h4>
                          <p className="text-sm text-gray-600">{member.email}</p>
                          <p className="text-xs text-gray-500 mt-1">Member for {member.days_as_member} days</p>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            member.risk_level === 'high' ? 'bg-red-100 text-red-800' :
                            member.risk_level === 'medium' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {member.risk_level.toUpperCase()} RISK
                          </Badge>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{member.risk_score}</p>
                          <p className="text-xs text-gray-500">Risk Score</p>
                        </div>
                      </div>

                      {/* Risk Factors */}
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Risk Factors:</p>
                        <div className="flex flex-wrap gap-1">
                          {member.risk_factors.map((factor, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {factor}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Recommendations */}
                      {member.recommendations.length > 0 && (
                        <div className="bg-teal-50 p-3 rounded-lg">
                          <p className="text-xs font-semibold text-teal-900 mb-2">
                            🎯 Recommended Actions:
                          </p>
                          <ul className="space-y-1">
                            {member.recommendations.map((rec, idx) => (
                              <li key={idx} className="text-sm text-teal-800 flex items-start gap-2">
                                <span className="text-teal-600 mt-0.5">•</span>
                                <span>{rec.suggestion}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feature Popularity Tab */}
        <TabsContent value="features" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Feature Popularity Analysis</CardTitle>
              <CardDescription>
                Discover which benefits and features are most valued by members
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Insights */}
              <div className="space-y-2 mb-6">
                {featureData?.insights?.map((insight, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-start gap-2 p-3 rounded-lg border ${getInsightColor(insight.type)}`}
                  >
                    {getInsightIcon(insight.type)}
                    <div>
                      <p className="text-sm font-semibold">{insight.message}</p>
                      <p className="text-xs mt-1">{insight.recommendation}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Top Benefits */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Most Popular Benefits</h3>
                <div className="space-y-2">
                  {featureData?.benefit_analysis?.slice(0, 10).map((benefit, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-teal-600">#{idx + 1}</span>
                          <span className="font-medium text-gray-900">{benefit.benefit}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-xs text-gray-600">
                            {benefit.total_members_with_access} members
                          </span>
                          <span className="text-xs text-gray-600">
                            {benefit.offered_in_plans} plan(s)
                          </span>
                          <span className="text-xs text-gray-600">
                            Avg price: ${benefit.average_plan_price.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">{benefit.popularity_score}</p>
                        <p className="text-xs text-gray-500">Score</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Appointment Types */}
              {featureData?.appointment_type_analysis && featureData.appointment_type_analysis.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Popular Appointment Types</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {featureData.appointment_type_analysis.map((apt, idx) => (
                      <div key={idx} className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg">
                        <p className="text-sm text-gray-600 capitalize">{apt.appointment_type}</p>
                        <p className="text-2xl font-bold text-teal-700">{apt.count}</p>
                        <p className="text-xs text-teal-600">{apt.percentage}% of total</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIAnalyticsDashboard;
